import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Tag } from '@indipia/ui-core';
import { worklogApi, withDevShareId } from '../api/worklogApi';
import { PhotoLightbox } from '../components/PhotoLightbox';
import { CustReqHistoryBlock } from '../components/custReq/CustReqHistoryBlock';
import type { FieldReportHistoryItem } from '../types/worklog.types';

/**
 * 반장 전송이력 조회 탭 — 기능명세 Z-2, API명세 §3.10.
 *
 * 이 공유링크로 본인이 제출한 보고만 표시된다(다른 작업요청 건은 스코프 밖).
 * 작성 만료 이후에도 조회는 허용된다 — 이미 보낸 것을 확인하는 행위이므로.
 *
 * 카드 우측 뱃지는 **처리 상태**다(설계서 S11~S13 §8.1 — 사장님 열람 확인/미확인은 폐기):
 * 진행중 / 작업완료 보고 '검수 대기 → 검수완료' / 이슈 보고 '이슈 → 조치완료'. 이슈 보고는 이슈유형명 뱃지도 함께 보인다(기획서 C-3).
 * 처리 상태(approvalYn·resolvedYn)는 **조회 시점 스냅샷**이다. 폴링·푸시로 실시간 갱신하지 않고,
 * 탭에 재진입할 때마다 재조회해 최신화한다(기능명세 Z-2-2 갱신 정책).
 */

/** 이력 카드의 처리 상태 뱃지 — 이슈 > 작업완료 > 진행중 우선순위(이슈가 있으면 작업완료여도 이슈 보고). 판정 불가(작업상태 미선택)면 null */
type HistoryStatus = { label: string; status: 'info' | 'warning' | 'success' | 'danger' | 'done' };
function historyStatus(it: FieldReportHistoryItem): HistoryStatus | null {
  if (it.issueTypeCodes?.length) {
    return it.resolvedYn === 'Y' ? { label: '조치완료', status: 'done' } : { label: '이슈', status: 'danger' };
  }
  if (it.workCmplYn === 'Y') {
    return it.approvalYn === 'Y' ? { label: '검수완료', status: 'success' } : { label: '검수 대기', status: 'warning' };
  }
  if (it.workCmplYn === 'N') return { label: '진행중', status: 'info' };
  return null;
}

/** ISO datetime → '2026.10.06 10:34'(24시간) — 카드 '작성일시'. 파싱 실패 시 원문 반환. */
function formatReportDtm(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 'YYYY-MM-DD' → '2026.10.06' — 카드 '작업일' */
function formatWorkDate(date: string): string {
  return date.slice(0, 10).replace(/-/g, '.');
}

/** 카드에 직접 노출할 썸네일 수 — 초과분은 마지막 자리에 '+N장' */
const THUMB_LIMIT = 4;

/** 무한 스크롤 페이지 크기 — 하단 도달 시 다음 페이지를 이어 붙인다(§3.10) */
const PAGE_SIZE = 10;

/**
 * [로컬 테스트 전용] 게스트 사진은 직접 <img src>로 로드된다 — 운영은 공유쿠키, 로컬은 shareId 쿼리 필요.
 * 응답 사진 URL에 dev shareId 를 붙여 반환한다.
 */
function mapPhotos(raw: FieldReportHistoryItem[]): FieldReportHistoryItem[] {
  return raw.map((it) => ({
    ...it,
    photos: it.photos.map((p) => ({
      ...p,
      thumbUrl: withDevShareId(p.thumbUrl) as string,
      url: withDevShareId(p.url) as string,
    })),
  }));
}

interface FieldReportHistoryTabProps {
  /** 값이 바뀌면 재조회한다 — 제출 성공 후 목록 최신화용 */
  reloadNonce: number;
  /** 조회 건수 보고 — 상위(현장 바)에서 'N/전체' 표기용. 로드마다 갱신된다 */
  onCountChange?: (count: { loaded: number; total: number }) => void;
}

export function FieldReportHistoryTab({ reloadNonce, onCountChange }: FieldReportHistoryTabProps) {
  const [state, setState] = useState<'loading' | 'error' | 'ready'>('loading');
  const [items, setItems] = useState<FieldReportHistoryItem[]>([]);
  /** 전체 전송 건수(뱃지·카운터 분모) — 조회된 items.length 와 함께 'N/전체' 표기 */
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageRef = useRef(0);
  /** 하단 감지 sentinel — 뷰포트에 들어오면 다음 페이지 로드(부모가 window 스크롤이라 컨테이너 onScroll 대신 사용) */
  const sentinelRef = useRef<HTMLDivElement>(null);
  /** 라이트박스 — 어느 카드의 몇 번째 사진인지 */
  const [lb, setLb] = useState<{ cardIdx: number; photoIdx: number } | null>(null);

  // 초기 로드 / 재조회(reloadNonce=제출 성공 등) — 항상 page 0부터 리셋
  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    pageRef.current = 0;
    worklogApi
      .getFieldReportHistoryShare(0, PAGE_SIZE, controller.signal)
      .then((res) => {
        const data = res.data.data;
        setItems(mapPhotos(data.items ?? []));
        setTotalCount(data.totalCount);
        setHasNext(data.hasNext);
        setState('ready');
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
        setState('error');
      });
    return () => controller.abort();
  }, [reloadNonce]);

  // 다음 페이지 이어붙이기 — 하단 근접 시 호출
  const loadMore = useCallback(() => {
    if (loadingMore || !hasNext) return;
    setLoadingMore(true);
    const next = pageRef.current + 1;
    worklogApi
      .getFieldReportHistoryShare(next, PAGE_SIZE)
      .then((res) => {
        const data = res.data.data;
        setItems((prev) => [...prev, ...mapPhotos(data.items ?? [])]);
        setTotalCount(data.totalCount);
        setHasNext(data.hasNext);
        pageRef.current = next;
      })
      .catch(() => {
        /* 추가 로드 실패는 조용히 — 다음 스크롤에서 재시도된다 */
      })
      .finally(() => setLoadingMore(false));
  }, [loadingMore, hasNext]);

  // 조회 건수 상위 보고 — ready 상태에서 items.length·totalCount 가 바뀔 때마다(로딩 중 0/0 깜빡임 방지).
  useEffect(() => {
    if (state === 'ready') onCountChange?.({ loaded: items.length, total: totalCount });
  }, [state, items.length, totalCount, onCountChange]);

  // 하단 sentinel이 뷰포트(200px 여유)에 들어오면 다음 페이지 로드. window/컨테이너 어느 쪽 스크롤이든 동작.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNext) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasNext, loadMore]);

  if (state === 'loading') {
    return (
      <div className="flex flex-1 items-center justify-center py-12 text-sm" style={{ color: 'var(--shell-hint)' }}>
        불러오는 중…
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-1 py-12 text-center">
        <p className="text-[13px] font-semibold" style={{ color: 'var(--shell-on-surface)' }}>
          전송이력을 불러올 수 없습니다
        </p>
        <p className="text-body-sm" style={{ color: 'var(--shell-on-surface-2)' }}>
          잠시 후 다시 시도해 주세요.
        </p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-1 py-12 text-center">
        <p className="text-[13px] font-semibold" style={{ color: 'var(--shell-on-surface)' }}>
          전송한 현장보고가 없습니다
        </p>
        <p className="text-body-sm" style={{ color: 'var(--shell-on-surface-2)' }}>
          현장보고 등록 탭에서 첫 보고를 작성해 주세요.
        </p>
      </div>
    );
  }

  const lbPhotos = lb === null ? [] : items[lb.cardIdx].photos;

  return (
    <>
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {items.map((it, cardIdx) => {
          // BE가 workTypeNames(공종명 배열)를 코드와 같은 순서로 내려준다. 이름 우선, 없으면 코드 폴백.
          const workTypeNames = it.workTypeNames?.length ? it.workTypeNames : it.workTypeCodes;
          const status = historyStatus(it);
          const issueNames = it.issueTypeNames?.length ? it.issueTypeNames : (it.issueTypeCodes ?? []);
          // 썸네일 최대 4개. 4개 초과면 3개만 노출하고 마지막 자리는 '+N장'(N=총 장수-3).
          const rest = it.photos.length > THUMB_LIMIT ? it.photos.length - (THUMB_LIMIT - 1) : 0;
          const shown = rest > 0 ? it.photos.slice(0, THUMB_LIMIT - 1) : it.photos.slice(0, THUMB_LIMIT);
          return (
            <article
              key={it.fieldReportId}
              className="mb-2 px-3.5 py-3"
              style={{
                background: 'var(--shell-surface)',
                border: 'var(--border-width-hairline) solid var(--shell-border)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              {/* 휴대폰 폭에서 이슈 뱃지가 여러 개면 한 줄에 다 못 들어간다 — 뱃지 묶음이 아래 줄로 내려가도록 wrap(날짜 라벨이 세로로 쪼개지지 않게 dl은 shrink-0) */}
              <div className="flex flex-wrap items-start justify-between gap-x-sm gap-y-1">
                {/* 작성일시(제출 시각) · 작업일 — 사장님 확인/공사현황 카드와 동일 표기. 목록 정렬은 작성일시(최신 제출순) 유지.
                    라벨 연하게·값 진하게, 값 시작 위치 정렬 */}
                <dl className="grid shrink-0 grid-cols-[auto_1fr] items-baseline gap-x-sm gap-y-sm whitespace-nowrap text-body-sm">
                  <dt className="text-text-muted">작성일시:</dt>
                  <dd className="font-medium text-text">{formatReportDtm(it.reportDtm)}</dd>
                  <dt className="text-text-muted">작업일:</dt>
                  <dd className="font-medium text-text">{formatWorkDate(it.workDate)}</dd>
                </dl>
                {/* 이슈유형명 뱃지(기획서 C-3 .hc-issue-badge) + 처리 상태 뱃지(둥근 pill). 색만이 아니라 텍스트 동반(D-07 접근성) */}
                {(status || issueNames.length > 0) && (
                  <div className="ml-auto flex min-w-0 flex-wrap justify-end gap-1">
                    {issueNames.map((name, i) => (
                      <Badge key={`issue-${name}-${i}`} status="warning" className="!rounded-full">{name}</Badge>
                    ))}
                    {status && <Badge status={status.status} className="!rounded-full">{status.label}</Badge>}
                  </div>
                )}
              </div>

              <p
                className="mt-1.5 text-body-sm leading-relaxed"
                style={{
                  color: 'var(--shell-on-surface-2)',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  whiteSpace: 'pre-line',
                }}
              >
                {it.reportContent}
              </p>

              {(workTypeNames.length > 0 || it.photos.length > 0) && (
                <div className="mt-2 flex items-center gap-1.5">
                  <div className="flex min-w-0 flex-wrap gap-1">
                    {/* .tag 기본 패딩(좌12·우6, 삭제버튼용 비대칭)을 대칭으로 덮어 텍스트 중앙 정렬 */}
                    {workTypeNames.map((name, i) => (
                      <Tag key={`${name}-${i}`} className="!px-2 !py-[3px] !text-[10px]">{name}</Tag>
                    ))}
                  </div>
                  {it.photos.length > 0 && (
                    <div className="ml-auto flex shrink-0 gap-1">
                      {shown.map((p, i) => (
                        <button
                          key={p.frPhotoId}
                          type="button"
                          aria-label={`사진 ${i + 1} 보기`}
                          onClick={() => setLb({ cardIdx, photoIdx: i })}
                          className="overflow-hidden"
                          style={{
                            width: 48, height: 48, padding: 0,
                            border: 'var(--border-width-hairline) solid var(--shell-border)',
                            borderRadius: 'var(--radius-sm)', background: 'var(--shell-skeleton)',
                            cursor: 'pointer',
                          }}
                        >
                          <img
                            src={p.thumbUrl}
                            alt=""
                            loading="lazy"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        </button>
                      ))}
                      {rest > 0 && (
                        <button
                          type="button"
                          aria-label={`사진 ${rest}장 더 보기`}
                          onClick={() => setLb({ cardIdx, photoIdx: shown.length })}
                          className="flex items-center justify-center text-caption font-bold"
                          style={{
                            width: 48, height: 48,
                            border: 'var(--border-width-hairline) solid var(--shell-border)', borderRadius: 'var(--radius-sm)',
                            background: 'var(--shell-skeleton)', color: 'var(--shell-on-surface-2)',
                            cursor: 'pointer',
                          }}
                        >
                          +{rest}장
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* 고객 요청(S16) — 이력 카드 아래 '고객 요청 n · 반영 n' + 요청별 반영(읽기 전용) */}
              {it.customerRequests && it.customerRequests.length > 0 && (
                <CustReqHistoryBlock items={it.customerRequests} summary={it.customerRequestSummary} />
              )}
            </article>
          );
        })}

        {/* 하단 감지 sentinel — 뷰포트 진입 시 다음 페이지 로드 */}
        {hasNext && <div ref={sentinelRef} aria-hidden style={{ height: 1 }} />}

        {/* 무한 스크롤 하단 상태 — 로딩/끝 표시 */}
        {loadingMore && (
          <div className="py-3 text-center text-body-sm" style={{ color: 'var(--shell-hint)' }}>
            불러오는 중…
          </div>
        )}
        {!hasNext && items.length > PAGE_SIZE && (
          <div className="py-3 text-center text-caption" style={{ color: 'var(--shell-on-surface-2)' }}>
            마지막 전송이력입니다
          </div>
        )}
      </div>

      <PhotoLightbox
        photos={lbPhotos}
        index={lb?.photoIdx ?? null}
        onClose={() => setLb(null)}
        onIndexChange={(i) => setLb((prev) => (prev ? { ...prev, photoIdx: i } : prev))}
      />
    </>
  );
}
